import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { ThreeDSecureFrame } from "../forms/ThreeDSecureFrame";

// --- NEW: socket.io ---
import { io, Socket } from "socket.io-client";
import { getOrderStatusManual } from "../../utils/order.utils";
import { SOCKET_URL, socketOptions } from "../../constants/apiRoutes";
import { FormShell } from "../forms/FormCell";
import { CardBrand, PaymentSocketResponse } from "../../types/payment";
import { BrandIcon } from "./BrandIcon";

// ---- Card brand detection & validation helpers ----

const BRAND_PATTERNS: Array<{ brand: CardBrand; regex: RegExp }> = [
  { brand: "visa", regex: /^4\d{0,}$/ },
  {
    brand: "mastercard",
    regex:
      /^(5[1-5]\d{0,}|2(2[2-9]\d{0,}|[3-6]\d{0,}|7([01]\d{0,}|20\d{0,})))$/,
  },
  { brand: "amex", regex: /^3[47]\d{0,}$/ },
  { brand: "jcb", regex: /^(35(2[89]|[3-8]\d)\d{0,})$/ },
  { brand: "unionpay", regex: /^62\d{0,}$/ },
];

function detectBrand(digitsOnly: string): CardBrand {
  if (!digitsOnly) return "unknown";
  for (const { brand, regex } of BRAND_PATTERNS) {
    if (regex.test(digitsOnly)) return brand;
  }
  return "unknown";
}

function luhnCheck(digitsOnly: string): boolean {
  if (digitsOnly.length < 12) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = digitsOnly.length - 1; i >= 0; i--) {
    let digit = parseInt(digitsOnly.charAt(i), 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

function formatCardNumber(digitsOnly: string, brand: CardBrand): string {
  if (brand === "amex") {
    const g1 = digitsOnly.slice(0, 4);
    const g2 = digitsOnly.slice(4, 10);
    const g3 = digitsOnly.slice(10, 15);
    return [g1, g2, g3].filter(Boolean).join(" ");
  }
  const groups = digitsOnly.match(/.{1,4}/g) ?? [];
  return groups.join(" ");
}

function maxLengthForBrand(brand: CardBrand): number {
  switch (brand) {
    case "amex":
      return 15;
    default:
      return 16;
  }
}

function cvcLengthForBrand(brand: CardBrand): number {
  return brand === "amex" ? 4 : 3;
}

function expiryIsValid(mmYY: string): boolean {
  const cleaned = mmYY.replace(/\s/g, "");
  const match = cleaned.match(/^(\d{2})\/?(\d{2})$/);
  if (!match) return false;
  let [_, mm, yy] = match;
  const month = parseInt(mm, 10);
  if (month < 1 || month > 12) return false;
  const year = 2000 + parseInt(yy, 10);
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const expMonth = new Date(year, month - 1, 1);
  return expMonth >= thisMonth;
}

interface PaymentPayload {
  orderId: any;
  paymentChannelId: string;
  cardNumber: string;
  cardExpiryMMYY: string;
  cvvCode: string;
  payerName: string;
  paymentTypeFromApp: string;
}

interface CreditCardPaymentProps {
  paymentChannelId: string;
  order: any;
  cusId?: string | number;
  onPaymentSuccess: (payload: any) => void;
  onPaymentError: (errMsg: string) => void;
  onCancel?: () => void;
  isActive: boolean;
  makePaymentAsync: (payload: PaymentPayload) => Promise<any>;
  isMakingCreditPayment: boolean;
  /** id of the underlying form so the footer's submit button can target it */
  formId?: string;
  /** report submit-readiness so the shared PaymentFooter can drive "Pay now" */
  onFooterStateChange?: (state: {
    canSubmit: boolean;
    processing: boolean;
  }) => void;
}

type PushStatus = "idle" | "pending" | "success" | "failed" | "expired";

const CreditCardPayment: React.FC<CreditCardPaymentProps> = ({
  paymentChannelId,
  order,
  onPaymentSuccess,
  onPaymentError,
  makePaymentAsync,
  onCancel,
  isActive,
  isMakingCreditPayment,
  formId = "credit-card-form",
  onFooterStateChange,
}) => {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [threeDSecureHtml, setThreeDSecureHtml] = useState<string | null>(null);
  const [isVerifying3DSecure, setIsVerifying3DSecure] = useState(false);

  // --- NEW: socket status indicator ---
  const [socketConnected, setSocketConnected] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus>("idle");

  // --- NEW: socket refs ---
  const socketRef = useRef<Socket | null>(null);

  const digitsOnly = (number || "").replace(/\D/g, "");
  const brand = useMemo(() => detectBrand(digitsOnly), [digitsOnly]);
  const maxLen = maxLengthForBrand(brand);
  const formattedNumber = useMemo(
    () => formatCardNumber(digitsOnly.slice(0, maxLen), brand),
    [digitsOnly, brand, maxLen]
  );

  const numberValid = digitsOnly.length === maxLen && luhnCheck(digitsOnly);
  const expiryValid = expiryIsValid(expiry);
  const cvcValid = /^\d+$/.test(cvc) && cvc.length === cvcLengthForBrand(brand);
  const nameValid = name.trim().length >= 2;

  const allValid = nameValid && numberValid && expiryValid && cvcValid;
  const shouldShowBrandIcon = brand !== "unknown" && digitsOnly.length >= 4;

  const canSubmit = allValid && !isMakingCreditPayment && !isVerifying3DSecure;

  function handleNumberInput(v: string) {
    const d = v.replace(/\D/g, "");
    const limited = d.slice(0, maxLen);
    setNumber(formatCardNumber(limited, brand));
  }

  function handleExpiryInput(v: string) {
    const d = v.replace(/\D/g, "").slice(0, 4);
    if (d.length === 1 && Number(d[0]) > 1) {
      setExpiry(`0${d[0]}`);
      return;
    }
    const mm = d.slice(0, 2);
    const yy = d.slice(2);
    setExpiry(yy ? `${mm}/${yy}` : mm);
  }

  // --- NEW: SOCKET attach/detach helpers ---
  const attachSocket = (ordId: string) => {
    // create or reuse socket
    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL, { ...socketOptions });
    }

    const s = socketRef.current;
    // @ts-ignore
    s._detach?.();

    const onConnect = () => {
      setSocketConnected(true);
      s.emit("subscribe", { orderId: ordId });
      console.log("subscribing to orderID: ", ordId);
      console.log(s);
    };
    const onOrderUpdate = (data: PaymentSocketResponse) => {
      const status = getOrderStatusManual(data);
      console.log("socket data:", data);

      if (status === "success") {
        // if (!handledSuccessRef.current) {
        //   handledSuccessRef.current = true;

        setPushStatus("success");

        onPaymentSuccess({
          ...data,
          paymentMethodData: { brand, last4: digitsOnly.slice(-4) },
        });
        // }
        return;
      }

      if (status === "failed") {
        setPushStatus("failed");
        onPaymentError(data?.message || data?.desc_payment || "Payment failed");
        return;
      }

      // if (status === "expired") {
      //   setPushStatus("expired");
      //   onPaymentError("Payment expired");
      //   return;
      // }

      setPushStatus("pending"); // default
      setIsVerifying3DSecure(false);
    };

    const onConnectError = (err: any) => {
      setSocketConnected(false);
      // don't hard-fail the UX; backend may still complete and later reconnect
    };

    const onDisconnect = () => {
      console.log("socketDisconnected");
      setSocketConnected(false);
    };

    s.on("connect", onConnect);
    s.on(ordId, onOrderUpdate);
    s.on("connect_error", onConnectError);
    s.on("disconnect", onDisconnect);

    // @ts-ignore
    s._detach = () => {
      s.off("connect", onConnect);
      s.off(ordId, onOrderUpdate);
      s.off("connect_error", onConnectError);
      s.off("disconnect", onDisconnect);
    };
  };

  const detachSocket = () => {
    const s = socketRef.current;
    if (s) {
      console.log("old: ", s);
      console.log("Detaching socket and disconnecting");
      // @ts-ignore
      s._detach?.();
      s.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    }
  };

  const handleCancelPayment = () => {
    detachSocket();
    setIsVerifying3DSecure(false);
    setThreeDSecureHtml(null);
    setPushStatus("idle");
    onCancel?.();
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allValid) return;

    try {
      setPushStatus("pending");

      const paymentDetails = {
        orderId: order.orderId,
        paymentChannelId,
        cardNumber: number.trim(),
        cardExpiryMMYY: expiry.replace("/", ""),
        cvvCode: cvc,
        payerName: name,
        paymentTypeFromApp: brand.toUpperCase(),
      };

      const response = await makePaymentAsync(paymentDetails);

      // const paymentRef = response?.payment_ref;

      if (response?.decodedHtml) {
        setIsVerifying3DSecure(true);
        setThreeDSecureHtml(response.decodedHtml);
        setPushStatus("pending");
      }
    } catch (err: any) {
      setPushStatus("failed");
      onPaymentError(err?.message ?? "Payment failed");
    }
  }

  useEffect(() => {
    // // Attach socket when component mounts or orderId changes
    attachSocket(order.orderId);
    return () => {
      // Detach socket when component unmounts or orderId changes
      detachSocket();
    };
  }, [order.orderId]);

  // Report submit-readiness up so the shared PaymentFooter can render "Pay now"
  const processing = isMakingCreditPayment || isVerifying3DSecure;
  useEffect(() => {
    onFooterStateChange?.({ canSubmit, processing });
  }, [canSubmit, processing, onFooterStateChange]);

  if (!isActive) {
    return (
      <FormShell elevation={0} sx={{ border: "none", padding: 0 }}>
        <Typography variant="body1" color="text.secondary">
          This payment method is currently inactive. Please choose another
          method.
        </Typography>
      </FormShell>
    );
  }

  // Compact inline status — only surfaced when there's something to say
  const STATUS_MAP: Record<
    Exclude<PushStatus, "idle">,
    { text: string; color: string }
  > = {
    pending: { text: "Waiting for confirmation…", color: "warning.main" },
    success: { text: "Payment successful", color: "success.main" },
    failed: { text: "Payment failed", color: "error.main" },
    expired: { text: "Payment expired", color: "error.main" },
  };
  const status = pushStatus === "idle" ? null : STATUS_MAP[pushStatus];

  return (
    <>
      {threeDSecureHtml && (
        <ThreeDSecureFrame
          decodedHtml={threeDSecureHtml}
          onClose={() => setThreeDSecureHtml(null)}
          onCancel={handleCancelPayment}
        />
      )}

      <FormShell
        elevation={0}
        sx={{ border: "none", p: 0, maxWidth: 420, mx: "auto", width: "100%" }}
      >
        {/* Header: title + brand, with a subtle live-connection dot */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Card details
            </Typography>
            {shouldShowBrandIcon && <BrandIcon brand={brand} />}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: socketConnected ? "success.main" : "text.disabled",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {socketConnected ? "Live" : "Reconnecting…"}
            </Typography>
          </Box>
        </Box>

        {/* Inline payment status — only when there's something to report */}
        {status && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: status.color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: status.color, fontWeight: 600 }}
            >
              {status.text}
            </Typography>
          </Box>
        )}

        <Box component="form" id={formId} noValidate onSubmit={handleSubmit}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                label="Name on card"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!name && !nameValid}
                helperText={!!name && !nameValid ? "Enter full name" : undefined}
                autoComplete="cc-name"
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Card number"
                size="small"
                fullWidth
                value={formattedNumber}
                onChange={(e) => handleNumberInput(e.target.value)}
                error={digitsOnly.length >= 12 && !numberValid}
                helperText={
                  digitsOnly && !numberValid ? "Invalid card number" : undefined
                }
                autoComplete="cc-number"
                inputMode="numeric"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Expiry (MM/YY)"
                size="small"
                fullWidth
                value={expiry}
                onChange={(e) => handleExpiryInput(e.target.value)}
                inputMode="numeric"
                inputProps={{ maxLength: 5, pattern: "[0-9]*" }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={`CVC${brand === "amex" ? " (4)" : ""}`}
                size="small"
                fullWidth
                value={cvc}
                onChange={(e) =>
                  setCvc(
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, cvcLengthForBrand(brand))
                  )
                }
                error={!!cvc && !cvcValid}
                helperText={!!cvc && !cvcValid ? "Invalid CVC" : undefined}
                autoComplete="cc-csc"
                inputMode="numeric"
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            mt: 2,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary">
            Securely processed — your full card number is never stored.
          </Typography>
        </Box>
      </FormShell>
    </>
  );
};

export default CreditCardPayment;
